<template>
  <div class="form-select__wrapper">
    <div
      :class="['form-select', (_is_select_opened) ? 'form-select_opened' : '', ($props.is_disabled) ? 'disabled' : '', ($props.is_error) ? 'error' : '',]"
      v-on-click-outside="closeSelect"
      ref="selectListElement"
    >
      <div v-if="$props.label" class="form-select__label">
        {{ $props.label }} <span class="form-select__required" v-show="$props?.is_required">*</span>
      </div>
      <button
          :class="['form-select__button', ($props.show_tags) ?? '_tags']"
          @click.prevent="openSelect()"
      >
      <span class="form-select-tags" v-if="$props.show_tags && _selected_value?.length">
        <span class="form-select-tags__item" v-for="item in _selected_value">
          <span class="form-select-tags__label"> {{item?.label}} </span>
          <icon-close class="form-select-tags__icon" @click.stop.prevent="deleteItem(item)"/>
        </span>
      </span>
        <span class="form-select__placeholder" v-else> {{ placeholder }} </span>
        <icon-arrow-down class="form-select__icon" />
      </button>
      <transition mode="out-in" name="fade">
        <div
          v-show="_is_select_opened"
          class="form-select__list"
          :class="[getListPosition,getListHorizontalPosition]"
        >
          <slot name="menu">
            <form-checkbox
              class="form-select__option"
              ref="checkbox"
              v-for="option in options"
              :key="option?.value"
              :value="_selected_value.some(item => item?.value === option?.value)"
              @click="selectItem(option)"
            >
              <template #label>
                {{ option?.label }}
              </template>
            </form-checkbox>
          </slot>
        </div>
      </transition>
    </div>
    <div v-if="error_message" class="form-select__error">
      {{ error_message }}
    </div>
  </div>
</template>

<script setup>
import { onMounted, ref, watch, computed } from "vue";
import { vOnClickOutside } from "@vueuse/components";
import IconArrowDown from "@/components/ui/icons/IconArrowDown.vue";
import IconClose from "@/components/ui/icons/IconClose.vue";

const $props = defineProps(["label", "placeholder", "options", "is_disabled", "show_tags", "is_error", "modelValue", "error_message","bindData","is_required", "multiple"])
const $emit = defineEmits(["update:modelValue", "validate","change"])

const _is_select_opened = ref(false)
const _selected_value = ref([])
const checkbox = ref(null)
const checkboxValues = ref([])
const selectListElement = ref(null)

const DROPDOWN_MAX_HEIGHT = 280
const getListPosition = computed(() => {
  return (window.innerHeight - selectListElement.value?.getBoundingClientRect().top < DROPDOWN_MAX_HEIGHT) ? 'active__list-top' : 'active__list-bottom'
})

const getListHorizontalPosition = computed(() => {
  return (selectListElement.value?.getBoundingClientRect().right + selectListElement.value?.getBoundingClientRect().width > window.innerWidth ) ? 'active__list-left' : ''
})

function openSelect() {
  _is_select_opened.value = !_is_select_opened.value
}

function closeSelect() {
  if (_is_select_opened.value === true) {
    _is_select_opened.value = false

    $emit("validate")
  }
}

function deleteItem(new_item) {
  _selected_value.value = _selected_value.value.filter((item) => item?.value !== new_item.value)

  $emit("update:modelValue", _selected_value.value)
  $emit("validate")
}

function selectItem(new_item) {
  if ($props.multiple === false) {
    if (_selected_value.value.find((item) => item?.value === new_item.value)) {
      _selected_value.value = []
    } else {
      _selected_value.value = [new_item]
    }
  } else {
    if (_selected_value.value.find((item) => item?.value === new_item.value)) {
      _selected_value.value = _selected_value.value.filter((item) => item?.value !== new_item.value)
    } else {
      _selected_value.value.push(new_item)
    }
  }
  
  $emit("update:modelValue", _selected_value.value.map((item) => item?.value))
  $emit("change", _selected_value.value)
}

watch($props, () => {
  if($props.modelValue){
    _selected_value.value = $props.modelValue
  }

  if($props.bindData?.length){
    _selected_value.value = []
    
    $props.bindData?.forEach((item) => {
      const index = $props.options.findIndex((option) => option.value === item)
      if(index !== -1) {
        _selected_value.value.push($props.options[index])
      }
    })
  }
})

onMounted(() => {
  if ($props.modelValue?.length) {
    _selected_value.value = $props.modelValue
  }
})
</script>

<style lang="scss" scoped>
  .form-select {
    position: relative;
    min-width: 160px;

    @include screenL {
      min-width: 120px;
    }

    &.disabled {
      .form-select__button {
        background-color: var(--background-color);
        user-select: none;
        pointer-events: none;
      }
    }

    &.error {
      .form-select__button {
        border-color: #F15A22;
        color: #F15A22;
      }
    }

    &_opened {

      .form-select__icon {
        transform: rotate(-180deg);
      }
    }

    &__label {
      font-size: 12px;
      line-height: 20px;
      margin: 0 0 4px 0;
    }

    &__required {
    margin: 0 0 0 4px;
    color: var(--error-color);
  }

    &__button {
      font-size: 12px;
      width: 100%;
      height: 40px;
      padding: 0 8px 0 16px;
      color: var(--font-color-two);
      border: 1px solid var(--border-color);
      border-radius: 8px;
      background-color: #FFF;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: space-between;
      transition: border .2s, box-shadow .2s;

      &:hover {
        border-color: var(--hover-color);
      }

      &:focus {
        border-color: var(--hover-color);
        box-shadow: 0 0 0 4px #E4E8F4;
      }
    }

    &-tags {
      display: flex;
      gap: 4px;
      margin: 8px 24px 8px 0;
      overflow-x: scroll;
      -ms-overflow-style: none;
      scrollbar-width: none;

      &::-webkit-scrollbar {
        display: none;
      }

      &__item {
        height: 32px;
        background-color: #F4F4FA;
        display: flex;
        justify-content: center;
        align-items: center;
        padding: 4px 16px;
        border-radius: 4px;
        border: 1px solid #E4E8F4;
      }

      &__label {
        max-width: 106px;
        text-align: center;
        text-overflow: ellipsis;
        overflow: hidden;
        white-space: nowrap;
      }

      &__icon {
        padding-left: 4px;
        width: 14px;
        height: 14px;
        flex-shrink: 0;
        ::v-deep(path){
          fill: #A3ABC0;
        }
      }
    }

    &__placeholder {
      color: var(--font-color-two);
    }

    &__icon {
      transition: transform .2s;
    }

    &__list {
      position: absolute;
      z-index: 5;
      top: calc(100% + 4px);
      left: 0;
      display: flex;
      flex-direction: column;
      border: 1px solid var(--border-color);
      border-radius: 8px;
      box-shadow: 0 25px 65px -15px #0811314d;
      background-color: #FFF;
      width: 260px;
      max-height: 280px;
      overflow-y: scroll;
      padding: 20px 24px;

      @include screenM {
        width: 100%;
        min-width: 312px;
      }

      &::-webkit-scrollbar {
        width: 8px;
      }

      &::-webkit-scrollbar-thumb {
        border-radius: 10px;
        background-color: var(--border-color);
      }
    }

    &__option {
      font-size: 12px;
      padding: 8px 0;
      transition: color .2s;
      background-color: #FFF;
      cursor: pointer;

      &:hover {
        color: var(--primary-color);
      }
    }

    &__error {
      font-size: 12px;
      line-height: 20px;
      color: var(--error-color);
    }
  }
  .active {
    &__list-top {
      top: unset;
      bottom: calc(100% + 4px);
    }
    &__list-left {
      left: -50%;
      right: unset;

      @include screenM {
        left: -110%;
      }
    }
  }
</style>
